import { useCallback, useMemo } from 'react';
import { FormattedNumber } from 'react-intl';
import { useObservable } from 'react-use-observable';

import { Dropdown, Pagination, Table as AntdTable, Tag, Typography } from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { formatDate } from 'helpers/date';
import { TableActionButton } from 'v2/components/Shared/TableActionButton';
import type { IChargebackList } from 'v2/interfaces/chargeback';
import chargebackService from 'v2/services/chargeback';

interface IProps {
  setModalVisible: (visible: boolean, invoiceId: number) => void;
}

const listStatus = {
  10: 'Aguardando Documentação',
  20: 'Em Análise',
  1: 'Em Processamento',
  3: 'Ganho',
  7: 'Perdido'
};

const List = ({ setModalVisible }: IProps) => {
  const [isLoading] = useObservable(() => chargebackService.isLoading(), []);
  const [data] = useObservable(() => chargebackService.getChargebacks(), []);
  const [pagination] = useObservable(() => chargebackService.getPaginationResponse(), []);

  const onPaginationChange = useCallback((page: number, pageSize: number) => {
    chargebackService.setPagination({ current_page: page, per_page: pageSize });
    document.querySelector('#chargeback_table')?.scrollIntoView();
  }, []);

  const columns: ColumnsType<IChargebackList> = useMemo(
    () => [
      {
        title: 'Fatura',
        key: 'invoice',
        width: 150,
        render: (_: unknown, chargeback) => (
          <div className='flex flex-col items-start gap-1'>
            <Typography.Text>{chargeback.invoice ? chargeback.invoice.id : '-'}</Typography.Text>
            <Tag className='-me-2'>{listStatus[chargeback.id as keyof typeof listStatus]}</Tag>
          </div>
        )
      },
      {
        title: 'Solicitado em',
        width: 120,
        dataIndex: 'requested_at',
        key: 'requested_at',
        render: (value: string) => <Typography.Text>{!!value && formatDate(value)}</Typography.Text>
      },
      {
        title: 'Cliente',
        key: 'customer',
        width: 200,
        render: (_: unknown, chargeback) => (
          <>
            <Typography.Text className='block'>{chargeback.customer?.name}</Typography.Text>
            <Typography.Text type='secondary' className='block'>
              {chargeback.customer?.email}
            </Typography.Text>
          </>
        )
      },
      {
        title: 'Produto',
        key: 'product',
        render: (_: unknown, chargeback) => <Typography.Text>{chargeback.product?.title}</Typography.Text>
      },
      {
        title: 'Afiliado',
        key: 'affiliate',
        width: 200,
        render: (_: unknown, chargeback) =>
          chargeback.affiliate?.name ? (
            <>
              <Typography.Text className='block'>{chargeback.affiliate?.name || '-'}</Typography.Text>
              <Typography.Text type='secondary' className='block'>
                {chargeback.affiliate?.email || '-'}
              </Typography.Text>
            </>
          ) : (
            '-'
          )
      },
      {
        title: 'Valor',
        key: 'amount',
        width: 150,
        align: 'right',
        render: (_: unknown, chargeback) => (
          <Typography.Text>
            <FormattedNumber value={chargeback.invoice ? chargeback.invoice.amount : 0} format='BRL' />
          </Typography.Text>
        )
      },
      {
        title: 'Limite',
        dataIndex: 'limit_date',
        key: 'limit_date',
        width: 120,
        render: (value: string) => <Typography.Text>{!!value && formatDate(value)}</Typography.Text>
      },
      {
        title: 'Ações',
        key: 'actions',
        width: 80,
        fixed: 'right',
        align: 'center',
        render: (_: unknown, chargeback) => {
          if (!chargeback.showUploadButton) {
            return null;
          }

          const items: MenuProps['items'] = [
            {
              key: chargeback.hasFiles ? 'view-files' : 'upload-file',
              label: chargeback.hasFiles ? 'Ver arquivos' : 'Subir arquivo',
              onClick: () => setModalVisible(true, chargeback.invoice.id)
            }
          ];

          return (
            <Dropdown menu={{ items }} trigger={['click']}>
              <TableActionButton onClick={() => undefined} />
            </Dropdown>
          );
        }
      }
    ],
    [setModalVisible]
  );

  return (
    <div id='chargeback_table' className='w-full overflow-x-auto overflow-y-hidden'>
      <AntdTable<IChargebackList>
        className='min-w-225 [&_.ant-table-body]:overflow-y-hidden [&_.ant-table-content]:overflow-y-hidden [&_.ant-table-tbody_.ant-typography]:text-sm'
        rowKey={chargeback => String(chargeback.invoice?.id)}
        columns={columns}
        dataSource={data ?? []}
        loading={!!isLoading}
        pagination={false}
        locale={{ emptyText: 'Nenhum registro encontrado.' }}
      />
      {pagination && (
        <div className='mt-4 flex justify-end'>
          <Pagination
            current={pagination.current_page || 1}
            total={pagination.total_items || 0}
            pageSize={pagination.per_page || 10}
            pageSizeOptions={['10', '20', '50', '100']}
            showSizeChanger
            onChange={onPaginationChange}
            onShowSizeChange={(page, size) => onPaginationChange(page, size)}
          />
        </div>
      )}
    </div>
  );
};

export default List;
